
import "./TestStyles.css";
import { handleOnPopState } from '../utils/pop_state_utils.js';
import React, { useState, useRef, useEffect, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { UpdateLocalStorage } from '../db/localstorage_utils.js';
import { playInstructionsOnce, handleAudioEndedCommon } from '../utils/audio_utils.js';
import { calculate_progress } from "../utils/progress_bar.js";
 

const QuestionNineScreen = ({ userData }) => {
  const [audio] = useState(new Audio("/audio/q9_instructions.mp3"));
  const [whiteScreenHeight, setWhiteScreenHeight] = useState(0);
  const [iconPositions, setIconPositions] = useState({
    paper: { x: 50, y: 290 },
    pencil: { x: 50, y: 380 },
  });

  const pencilRef = useRef(null);
  const paperRef = useRef(null);
  const [failed, setFailed] = useState(false);
  const activeIconRef = useRef(null);
  const initialOffsetRef = useRef({ x: 0, y: 0 });
  const [iconWidth, setIconWidth] = useState(0);
  const [iconHeight, setIconHeight] = useState(0);
  const whiteScreenRef = useRef(null);
  const containerRef = useRef(null);
  const [buttonDisabled, setButtonDisabled] = useState(true);
  const [nextButtonStyle, setNextButtonStyle] = useState("NextButton");
  const [audioPlayed, setAudioPlayed] = useState(false);
  const [questionPassed, setQuestionPassed] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const results = JSON.parse(localStorage.getItem("results"));
  const [nextPage] = useState("/Q9BScreen");        // Where this screen will navigate to next
  const [thisPage] = useState("screen_9");  
  const handleAudioEnded = useCallback(() => {
    handleAudioEndedCommon({
      audio,
      onEnded: handleAudioEnded,
      setAudioPlayed,
      setButtonDisabled,
      setNextButtonStyle
    });
  }, [audio]);

   
  // Implementing the popstate handler
  window.onpopstate = () => {
    handleOnPopState({ results, userData, navigate, location });
  };
  
  /* The `useEffect` hook makes sure that the instruction audio is played only once. */
  useEffect(() => {
    if (!audioPlayed) {
      playInstructionsOnce({ audio, audioPlayed, setAudioPlayed, onEnded: handleAudioEnded });
    }
    const moveableIcon = document.querySelector(".moveableIcon");
    const styles = window.getComputedStyle(moveableIcon);
    const widthInVh = parseFloat(styles.width);
    setIconWidth(widthInVh);
    const heightInVh = parseFloat(styles.height);
    setIconHeight(heightInVh);
    const ContainerHeight = containerRef.current.getBoundingClientRect().height;
    setWhiteScreenHeight(whiteScreenRef.current.getBoundingClientRect().height);

    const initialPaperY = ContainerHeight - ContainerHeight/1.7; 
    const initialPencilY = ContainerHeight - ContainerHeight/2.15;

    setIconPositions({
      paper: { x: 50, y: initialPaperY },
      pencil: { x: 50, y: initialPencilY },
    });
  }, [audio, audioPlayed, handleAudioEnded]);

  /**
   * The `handleMouseDown` function is a JavaScript function that handles the mouse down event and
   * updates the position of an icon on a white screen.
   * @param event - The event parameter is the event object that is passed when the mouse down event is
   * triggered. It contains information about the event, such as the mouse coordinates and the target
   * element.
   * @param icon - The `icon` parameter represents the icon that was clicked or selected by the user.
   */
  const handleMouseDown = (event, icon) => {
    event.preventDefault();

    const { clientX, clientY } = getCoordinates(event);
    const whiteScreenRect = whiteScreenRef.current.getBoundingClientRect();

    const offsetX = clientX - whiteScreenRect.left;
    const offsetY = clientY - whiteScreenRect.top;

    setIconPositions((prevPositions) => ({
      ...prevPositions,
      [icon]: { x: offsetX, y: offsetY },
    }));

    activeIconRef.current = icon;
    initialOffsetRef.current = { x: offsetX, y: offsetY };

    document.addEventListener("mousemove", handleMouseMove);
    document.addEventListener("mouseup", handleMouseUp);
  };

  /**
   * The `handleMouseMove` function updates the position of an active icon based on the mouse movement
   * within a specified area.
   * @param event - The event parameter is an object that represents the mousemove event. It contains
   * information about the event, such as the position of the mouse cursor and any other relevant data.
   * @returns the updated icon positions.
   */
  const handleMouseMove = (event) => {
    event.preventDefault();

    if (!activeIconRef.current) return;

    const { clientX, clientY } = getCoordinates(event);
    const whiteScreenRect = whiteScreenRef.current.getBoundingClientRect();

    const offsetX = clientX - whiteScreenRect.left;
    const offsetY = clientY - whiteScreenRect.top;


    const initialX = iconPositions[activeIconRef.current].x;
    const initialY = iconPositions[activeIconRef.current].y;

    const maxX = whiteScreenRect.width - iconWidth;
    const maxY = whiteScreenRect.height - iconHeight;

    const newX = Math.max(Math.min(initialX + offsetX - initialOffsetRef.current.x, maxX), 0);
    const newY = Math.max(Math.min(initialY + offsetY - initialOffsetRef.current.y, maxY), 0);

    setIconPositions((prevPositions) => ({
      ...prevPositions,
      [activeIconRef.current]: { x: newX, y: newY },
    }));
  };
  
  /**
   * The function `getBoundingBox` returns the coordinates of the bounding box of a given object.
   * @param ref - The object in question.
   * @returns The function `getBoundingBox` returns an object with the properties `left`, `top`,
   * `right`, and `bottom`. These properties represent the coordinates of the bounding box of the
   * element referenced by `ref`.
   */
  const getBoundingBox = (ref) => {
    const element = ref.current;
    const { x, y, width, height } = element.getBoundingClientRect();
    return { left: x, top: y, right: x + width, bottom: y + height };
  };
  
  /**
   * The function handles the mouse up event and checks if the pencil and paper are in the correct
   * position on the white screen.
   */
  const handleMouseUp = () => {
    activeIconRef.current = null;
  
    document.removeEventListener("mousemove", handleMouseMove);
    document.removeEventListener("mouseup", handleMouseUp);
    document.removeEventListener("touchmove", handleMouseMove);
    document.removeEventListener("touchend", handleMouseUp);
  
    const paperBoundingBox = getBoundingBox(paperRef);
    const pencilBoundingBox = getBoundingBox(pencilRef);
    const whiteScreenBoundingBox = getBoundingBox(whiteScreenRef);
    const pencilHeight = pencilRef.current.getBoundingClientRect().height;
    const paperHeight = paperRef.current.getBoundingClientRect().height;
    setWhiteScreenHeight(whiteScreenRef.current.getBoundingClientRect().height);
  
    if ((paperBoundingBox.top + paperHeight/1.2) < (whiteScreenBoundingBox.bottom - whiteScreenHeight/2) && ((pencilBoundingBox.top + pencilHeight/1.2) > (whiteScreenBoundingBox.bottom - whiteScreenHeight/2))) {
      setFailed(true);
    }
    if ((pencilBoundingBox.top + pencilHeight/1.2) < (whiteScreenBoundingBox.bottom - whiteScreenHeight/2) && ((paperBoundingBox.top + paperHeight/1.2) < (whiteScreenBoundingBox.bottom - whiteScreenHeight/2)) && !failed) {
      setQuestionPassed(true);
    }
  };

  /**
   * The function `getCoordinates` returns the client coordinates of a mouse or touch event.
   * @param event - The `event` parameter is an object a mouse click or touch event.
   * @returns The function `getCoordinates` returns the x and y coordinates of the event.
   */
  const getCoordinates = (event) => {
    if (event.type.startsWith("touch")) {
      const touch = event.touches[0];
      return { clientX: touch.clientX, clientY: touch.clientY };
    }

    return { clientX: event.clientX, clientY: event.clientY };
  };

  /**
   * The function `handleTouchStart` is used to handle the touchstart event, call the 
   * handleMouseDown function, and add event listeners to the mouse.
   * @param event - The "event" parameter represents the event that triggered the function.
   * @param icon - The "icon" parameter is a variable that represents the icon element that was
   * touched.
   */
  const handleTouchStart = (event, icon) => {
    handleMouseDown(event, icon);
    document.addEventListener("touchmove", handleMouseMove, { passive: false });
    document.addEventListener("touchend", handleMouseUp);
  };

  return (
    <div className="Container" ref={containerRef}>
      <div className="InstructionsContainer">
        <img className="CharacterIcon" src="/icons/other.png" alt="Character Icon" />
        <progress variant="custom" className="ProgressBar" value={calculate_progress('asymptotic', 16, 54)} max="1"></progress>
        <p className="Instructions">Move the pencil across the line, then the paper too.</p>
      </div>
      <div className="whiteScreen" ref={whiteScreenRef}>
      <img
          className="moveableIcon"
          src="/icons/pencil.png"
          alt="Pencil"
          style={{
            transform: `translate(${iconPositions.pencil.x}px, ${iconPositions.pencil.y}px)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "pencil")}
          onTouchStart={(e) => handleTouchStart(e, "pencil")}
          ref={pencilRef}
        />
        <img
          className="moveableIcon"
          src="/icons/paper.png"
          alt="Paper"
          style={{
            transform: `translate(${iconPositions.paper.x}px, ${iconPositions.paper.y}px)`,
          }}
          onMouseDown={(e) => handleMouseDown(e, "paper")}
          onTouchStart={(e) => handleTouchStart(e, "paper")}
          ref={paperRef}
        />
      </div>

      <div className="NextButtonContainer">
      <Link to={nextPage}>
          <button disabled={buttonDisabled} onClick={() => UpdateLocalStorage(questionPassed, thisPage)} className={nextButtonStyle}>
            <img src="/icons/arrow.png" alt="Next Button" />
          </button>
        </Link>
      </div>
    </div>
  );
};

export default QuestionNineScreen;
